import { useState, useRef, useEffect } from 'react';
import { PasswordInput, InputField, Link, Button, Paragraph } from '@ets/design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from '_common/hooks';
import { signIn, clearFeedback } from 'Auth/redux/authSlice';
import SavedAccount from '../SavedAccount/SavedAccount';

type SelectedAccountLoginProps = {
  account: ParsedProfile<Account>;
  chooseAnother: () => void;
};

const SelectedAccountLogin = ({ account, chooseAnother }: SelectedAccountLoginProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [password, setPassword] = useState('');
  const passwordRef = useRef<HTMLInputElement>(null);
  const loading = useSelector((state) => state.auth.loading);
  const feedback = useSelector((state) => state.auth.feedback);

  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  useEffect(() => {
    if (feedback?.type === 'error') {
      passwordRef.current?.focus();
    }
  }, [feedback?.type]);

  const handleSignin = () => {
    dispatch(clearFeedback());
    dispatch(signIn({ params: { username: account.username, password } }));
  };

  return (
    <div>
      <div data-testid={'selected-saved-account'}>
        <SavedAccount account={account} removing={false} />
      </div>
      <div className="my-8">
        {/** This form wrap avoids browser verbose displaying password value
         * And it is needed to prevent the form from submitting on suffix click(eye icon)
         */}
        <form onSubmit={(e) => e.preventDefault()}>
          <InputField
            size="l"
            label={intl.formatMessage({ id: 'PASSWORD' })}
            suffix={
              <Link testId="forgot-password" size="s" variant="primary" href="/auth/recover">
                <Paragraph size="s" weight="500" className="pb-2 pl-8">
                  <FormattedMessage id="FORGOT_YOUR_PASSWORD" />
                </Paragraph>
              </Link>
            }
            testId="password"
          >
            <PasswordInput
              hasError={feedback?.type === 'error'}
              ref={passwordRef}
              size="l"
              value={password}
              autoComplete="off"
              onChange={(e) => setPassword(e.target.value)}
              placeholder={intl.formatMessage({ id: 'ENTER_PASSWORD' })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSignin();
                }
              }}
              testId="selected-saved-account-password"
            />
          </InputField>
        </form>
      </div>
      <Button
        variant="primary"
        fullWidth
        onClick={handleSignin}
        size="l"
        loading={loading}
        disabled={password.length === 0}
        testId="selected-saved-account-signin"
      >
        <FormattedMessage id="SIGN_IN" />
      </Button>
      <Button
        fullWidth
        variant="standard"
        onClick={chooseAnother}
        size="l"
        style={{ margin: '1rem 0 0 0' }}
        testId="sign-in-with-another-account"
      >
        <FormattedMessage id="SIGN_IN_WITH_ANOTHER_ACCOUNT" />
      </Button>
    </div>
  );
};

export default SelectedAccountLogin;
